import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getToken } from '../utils/auth';

/* ─────────────────────────────────────────────────────────────────────────────
   Favorilerim — kullanıcının sol menüde en üste sabitlediği sayfalar.
   Sunucuda tutulur (/api/Menu/Favorites) → kullanıcı hangi cihazdan girerse
   girsin favorileri yanında gelir. Toggle iyimserdir (yıldız anında dolar,
   istek arkada gider; hata olursa eski hale döner).
   ──────────────────────────────────────────────────────────────────────────── */

const FavoritesContext = createContext({
  favoriteIds: [],
  isFavorite: () => false,
  toggle: () => {},
  loading: false,
});

export function FavoritesProvider({ children }) {
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchFavorites = useCallback(async () => {
    const token = getToken();
    if (!token) { setFavoriteIds([]); return; }
    setLoading(true);
    try {
      const res = await fetch('/api/Menu/Favorites', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const ids = Array.isArray(data) ? data : (data?.data ?? []);
        setFavoriteIds(ids.map(Number).filter((n) => !Number.isNaN(n)));
      } else {
        setFavoriteIds([]);
      }
    } catch {
      setFavoriteIds([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFavorites();
    const onLogin = () => fetchFavorites();
    const onLogout = () => setFavoriteIds([]);
    window.addEventListener('auth:login', onLogin);
    window.addEventListener('auth:logout', onLogout);
    return () => {
      window.removeEventListener('auth:login', onLogin);
      window.removeEventListener('auth:logout', onLogout);
    };
  }, [fetchFavorites]);

  const toggle = useCallback(async (menuId) => {
    const id = Number(menuId);
    if (!id || Number.isNaN(id)) return;
    const token = getToken();
    if (!token) return;

    const wasFavorite = favoriteIds.includes(id);
    const previous = favoriteIds;
    // İyimser güncelleme — yıldız beklemeden dolar/boşalır.
    setFavoriteIds(wasFavorite ? previous.filter((x) => x !== id) : [...previous, id]);

    try {
      const res = await fetch(`/api/Menu/Favorites/${id}`, {
        method: wasFavorite ? 'DELETE' : 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) setFavoriteIds(previous);   // sunucu reddetti → geri al
    } catch {
      setFavoriteIds(previous);                // ağ hatası → geri al
    }
  }, [favoriteIds]);

  const isFavorite = useCallback((menuId) => favoriteIds.includes(Number(menuId)), [favoriteIds]);

  const value = useMemo(
    () => ({ favoriteIds, isFavorite, toggle, loading, reload: fetchFavorites }),
    [favoriteIds, isFavorite, toggle, loading, fetchFavorites],
  );

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export function useFavorites() {
  return useContext(FavoritesContext);
}
